
import * as React from "react"
import * as LabelPrimitive from "@radix-ui/react-label"
import { Slot } from "@radix-ui/react-slot"
import {
  Controller,
  ControllerProps,
  FieldPath,
  FieldValues,
  FormProvider,
  useFormContext,
} from "react-hook-form"

import { cn } from "@/lib/utils"
import { Label } from "@/components/ui/label"

const Form = FormProvider

type FormFieldContextValue<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>
> = {
  name: TName
}

const FormFieldContext = React.createContext<FormFieldContextValue>(
  {} as FormFieldContextValue
)

const FormField = <
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>
>({
  ...props
}: ControllerProps<TFieldValues, TName>) => {
  return (
    <FormFieldContext.Provider value={{ name: props.name }}>
      <Controller {...props} />
    </FormFieldContext.Provider>
  )
}

// Define a consistent return type for useFormField
type FormFieldInfo = {
  id: string;
  name: string;
  formItemId: string;
  formDescriptionId: string;
  formMessageId: string;
  error: any; // Changed from optional to required
}

const useFormField = () => {
  const fieldContext = React.useContext(FormFieldContext)
  const itemContext = React.useContext(FormItemContext)
  const formContext = useFormContext()

  // Added null/undefined check to prevent the destructuring error
  if (!fieldContext) {
    throw new Error("useFormField should be used within <FormField>")
  }

  // Check if form context exists before destructuring
  if (!formContext) {
    // Ensure we return an object with the same shape as the normal path
    const fallbackResult: FormFieldInfo = {
      id: itemContext?.id || "",
      name: fieldContext.name,
      formItemId: `form-item-${fieldContext.name}`,
      formDescriptionId: `form-item-description-${fieldContext.name}`,
      formMessageId: `form-item-message-${fieldContext.name}`,
      error: undefined, // Error is now required but can be undefined
    }
    return fallbackResult
  }

  // Original code path when form context exists
  const { getFieldState, formState } = formContext
  const fieldState = getFieldState(fieldContext.name, formState)

  const { id } = itemContext || { id: "" }

  const result: FormFieldInfo = {
    id,
    name: fieldContext.name,
    formItemId: `${id}-form-item`,
    formDescriptionId: `${id}-form-item-description`,
    formMessageId: `${id}-form-item-message`,
    error: fieldState.error, // Error is now always defined (might be undefined value)
  }

  return result
}

type FormItemContextValue = {
  id: string
}

const FormItemContext = React.createContext<FormItemContextValue | undefined>(
  undefined
)

const FormItem = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => {
  const id = React.useId()

  return (
    <FormItemContext.Provider value={{ id }}>
      <div ref={ref} className={cn("space-y-2", className)} {...props} />
    </FormItemContext.Provider>
  )
})
FormItem.displayName = "FormItem"

const FormLabel = React.forwardRef<
  React.ElementRef<typeof LabelPrimitive.Root>,
  React.ComponentPropsWithoutRef<typeof LabelPrimitive.Root>
>(({ className, ...props }, ref) => {
  // Use the hook safely with try/catch to prevent crashes
  let error = undefined;
  let formItemId = "";
  
  try {
    const field = useFormField();
    error = field.error;
    formItemId = field.formItemId;
  } catch (e) {
    // Silently handle the error - the component will render without form context
  }

  return (
    <Label
      ref={ref}
      className={cn(error && "text-destructive", className)}
      htmlFor={formItemId}
      {...props}
    />
  )
})
FormLabel.displayName = "FormLabel"

const FormControl = React.forwardRef<
  React.ElementRef<typeof Slot>,
  React.ComponentPropsWithoutRef<typeof Slot>
>(({ ...props }, ref) => {
  // Use the hook safely with try/catch
  let field = {
    error: undefined,
    formItemId: "",
    formDescriptionId: "",
    formMessageId: "",
  };
  
  try {
    field = useFormField();
  } catch (e) {
    // Silently handle the error
  }

  return (
    <Slot
      ref={ref}
      id={field.formItemId}
      aria-describedby={
        !field.error
          ? `${field.formDescriptionId}`
          : `${field.formDescriptionId} ${field.formMessageId}`
      }
      aria-invalid={!!field.error}
      {...props}
    />
  )
})
FormControl.displayName = "FormControl"

const FormDescription = React.forwardRef<
  HTMLParagraphElement,
  React.HTMLAttributes<HTMLParagraphElement>
>(({ className, ...props }, ref) => {
  // Use the hook safely with try/catch
  let formDescriptionId = "";
  
  try {
    const field = useFormField();
    formDescriptionId = field.formDescriptionId;
  } catch (e) {
    // Silently handle the error
  }

  return (
    <p
      ref={ref}
      id={formDescriptionId}
      className={cn("text-sm text-muted-foreground", className)}
      {...props}
    />
  )
})
FormDescription.displayName = "FormDescription"

const FormMessage = React.forwardRef<
  HTMLParagraphElement,
  React.HTMLAttributes<HTMLParagraphElement>
>(({ className, children, ...props }, ref) => {
  // Use the hook safely with try/catch
  let error = undefined;
  let formMessageId = "";
  
  try {
    const field = useFormField();
    error = field.error;
    formMessageId = field.formMessageId;
  } catch (e) {
    // Silently handle the error
  }

  const body = error ? String(error?.message) : children

  if (!body) {
    return null
  }

  return (
    <p
      ref={ref}
      id={formMessageId}
      className={cn("text-sm font-medium text-destructive", className)}
      {...props}
    >
      {body}
    </p>
  )
})
FormMessage.displayName = "FormMessage"

export {
  useFormField,
  Form,
  FormItem,
  FormLabel,
  FormControl,
  FormDescription,
  FormMessage,
  FormField,
}
